/**
 * TRIPVENTURA LINK LOCALIZATION UTILITY
 * * Purpose: 
 * Automatically intercepts and updates internal text links (especially inside Shopify 
 * Rich Text Editors/Blog Articles) to preserve the user's active language subfolder.
 * If a customer reads a Danish article, clicking an internal link keeps them in '/da/'.
 * * Strategy: Option B (Stored cleanly in Shopify Assets as an asynchronous script).
 */

(function () {
	// 1. Language subfolders, supplied by Liquid from `localization.available_languages`
	//    in layout/theme.liquid (see `window.tvLocales`).
	//
	//    This used to be a hardcoded array, which had drifted: it listed 'ar' —
	//    a language with no locale file and deliberately excluded from link
	//    rewriting until its RTL work is done — while omitting 'de', which is
	//    published. Anything missing from the list silently got NO localization,
	//    so German readers were being sent to English pages.
	//
	//    Driving it from Liquid means publishing a new market (Polish, Dutch,
	//    French) needs no code change here. The literal below is only a fallback
	//    for the case where the global failed to render.
	const SUPPORTED_LOCALES = Array.isArray(window.tvLocales) && window.tvLocales.length
		? window.tvLocales
		: ['tr', 'ru', 'da', 'de'];

	// 2. Destructure and grab the current page's domain (origin) and URL path (pathname)
	const { origin, pathname } = window.location;

	// 3. Scan the URL to determine if the user is currently browsing within a supported language folder
	const currentLocale = SUPPORTED_LOCALES.find((l) => pathname === '/' + l || pathname.startsWith('/' + l + '/'));

	// 4. Guard Clause: If the user is on the default site (English / no subfolder matched), stop executing entirely to save system resources
	if (!currentLocale) return;

	// 5. Construct the localized directory segment string (e.g., '/da' or '/tr') to append later
	const localePrefix = '/' + currentLocale;

	// 6. Regex rule to identify and ignore non-standard links (page anchors, email hotlinks, telephone calls, SMS targets, or raw scripts)
	const SKIP = /^(#|mailto:|tel:|sms:|javascript:|data:)/i;

	// 7. Helper helper function to verify if an incoming string path already has an active language subfolder attached to it
	const hasLocale = (p) => SUPPORTED_LOCALES.some((l) => p === '/' + l || p.startsWith('/' + l + '/'));

	// 8. Core function tasked with evaluating and transforming an individual link element
	function localizeAnchor(a) {
		// Idempotency Check: If this specific link has already been modified in a previous loop, skip it to prevent loops like /da/da/
		if (a.dataset.localized) return;

		// Stamp a hidden marker tracking attribute onto the link so the script knows it has safely processed this element
		a.dataset.localized = '1';

		// Extract the raw target address configuration value string from the link's HTML attribute
		const href = a.getAttribute('href');

		// If the link is missing text entirely or explicitly matches our standard exclusion rules (SKIP), drop out early
		if (!href || SKIP.test(href)) return;

		let url;
		try {
			// Safely parse the raw target string into a standardized, relative browser URL object structure
			url = new URL(href, origin);
		} catch (e) {
			// If parsing fails due to a broken or corrupt link format, abort fixing this specific element
			return;
		}

		// Domain Check: If the link leaves your website completely, or already contains an active language layout prefix, ignore it
		if (url.origin !== origin || hasLocale(url.pathname)) return;

		// Inject your store's active language subfolder directly to the front of the text URL path string
		url.pathname = localePrefix + (url.pathname === '/' ? '' : url.pathname);

		// Apply the newly rewritten localized URL directly back onto the clickable HTML element
		a.setAttribute('href', url.toString());
	}

	// 9. Operational scanner function that handles locating links across chosen parent layouts
	function localizeLinks(root) {
		// If no target area is defined, fall back safely to scanning the layout tree of the entire page document
		root = root || document;

		// If the root item being checked is a singular link element itself, evaluate it directly
		if (root.nodeType === 1 && root.matches && root.matches('a[href]')) localizeAnchor(root);

		// Locate all clickable hyperlink items within this container and pass them down to the updater
		if (root.querySelectorAll) root.querySelectorAll('a[href]').forEach(localizeAnchor);
	}

	// 10. Expose the execution framework globally onto the browser window object so dynamic AJAX apps can trigger it manually
	window.localizeLinks = localizeLinks;

	// 11. Wait for the browser to successfully construct the initial page layout map before firing optimization routines
	document.addEventListener('DOMContentLoaded', () => {
		// Process and correct all native text block links loaded into view right away
		localizeLinks(document);

		// 12. Create a background MutationObserver to intercept links that show up after initial loading (Predictive Search, Cart Drawers, Infinite Scroll)
		new MutationObserver((mutations) => {
			// Scan every layout update frame generated dynamically on the live web application page
			for (const m of mutations) {
				// Review nodes that have just been generated inside the viewport tree
				for (const node of m.addedNodes) {
					// Verify it is a valid HTML Element (nodeType 1) before sending it to the parser engine
					if (node.nodeType === 1) localizeLinks(node);
				}
			}
		}).observe(document.body, { childList: true, subtree: true }); // Watch structural code updates across the full webpage body
	});
})();
